import React, {useEffect} from 'react';
import {Button, ButtonGroup, ClickAwayListener, Fade, Grid, IconButton, Paper, Popper, TableCell, Typography} from "@material-ui/core";
import {AddCircleOutline, Delete, Edit, Settings} from "@material-ui/icons";
import {Link} from "react-router-dom";
import Helpers, {getCompanyNames} from "../../helpers";
import Auth from "../../auth";
import moment from "moment";
import CatalogCheckbox from "../../catalogmanager/components/EnhancedCheckbox";
import {EventIdColumn} from "../../catalogmanager/containers/catalog/components/catalog_columns";
import {useGlobalStyles} from "../../catalogmanager/utils/styles";
import {ClipboardCopyIcon, ExportIcon, ImportIcon} from "../../catalogmanager/utils/icons";
import CatalogTab from "../../catalogmanager/containers/catalog/components/catalog_tab";
import ContentLoader from "react-content-loader";
import EnhancedTable from "../../general/table";
import CatalogEventResults from "../../catalogmanager/containers/catalog/components/catalog_event_results";
import CatalogManagerResults, {useStyles} from "../../catalogmanager/components/CatalogManagerResults";
import Config from "../../config";
import EnhancedSelect from "../../catalogmanager/components/EnhancedSelect";
import InventoryEntity from './components/inventory_entity';
import {utilCheckInventory, utilDeleteInventory, utilGetInventorySummary, utilGetPartners, utilUpdateInventory} from '../../catalogmanager/utils/utils';
import axios from 'axios';
import EnhancedErrorDialog from '../../catalogmanager/components/EnhancedErrorDialog';
import EnhancedConfirmDialog from '../../catalogmanager/components/EnhancedConfirmDialog';
import UpdatingDialog from '../../catalogmanager/components/Dialogs/UpdatingDialog';
import EnhancedTextField from '../../catalogmanager/components/EnhancedTextField';
import {Alert} from '@material-ui/lab';
import { GetRuleDescription } from '../validation';

function InventoryEventsStatus(props) {
    const {partners, refreshInventorySummary} = props;
    const {table} = props;

    const [selectedPartner, setSelectedPartner] = React.useState(null);

    //#region Lifecycle
    useEffect(() => {
        // Set selected partner from the table state
        if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
            const filter = table.state.filter.filters.find((filter) => filter.field === "PartnerCoId");
            if (filter) {
                const partner = partners.find((p) => p.Id === filter.value);
                if (partner) {
                    setSelectedPartner({
                        label: partner.CompanyName,
                        value: partner.Id
                    });
                    refreshInventorySummary(partner.Id);
                }
            }
        } else {
            setSelectedPartner(null);
            refreshInventorySummary(null);
        }

        // eslint-disable-next-line
    }, [table.state.filter]);
    //#endregion

    return (
        <>
            <EnhancedSelect
                isClearable
                placeholder={"Select partner..."}
                containerStyle={{minWidth: "100px"}}
                options={
                    partners.map((partner) => ({
                        label: partner.CompanyName,
                        value: partner.Id
                    }))
                }
                value={selectedPartner}
                onChange={(value) => {
                    if (!value) {
                        // Remove all partner-related filters
                        const oldFilters = (table.state.filter?.filters || []).filter(
                            (filter) => filter.field !== "PartnerCoId"
                        );
                        table.extensions.replaceFilters(oldFilters);
                        setSelectedPartner(null);
                        refreshInventorySummary(null);
                    } else {
                        // Remove old partner-related filters
                        const oldFilters = (table.state.filter?.filters || []).filter(
                            (filter) => filter.field !== "PartnerCoId"
                        );
                        table.extensions.replaceFilters([
                            ...oldFilters,
                            {
                                field: "PartnerCoId",
                                operator: 'eq',
                                value: value.value,
                                static: true
                            }
                        ]);
                        setSelectedPartner(value);
                        refreshInventorySummary(value.value);
                    }
                }}
            />
        </>
    );
}

function InventoryStatus(props) {
    const {partners, setInventoryStatusSelection, inventoryStatusSelection} = props;
    const {table, isRetailer} = props;

    const [selectedPartner, setSelectedPartner] = React.useState(null);
    const [entityCount, setEntityCount] = React.useState(0);

    // Helper function to recursively remove filters by field name
    const removeFiltersByField = (filters, fieldToRemove) => {
        if (!filters) return [];

        return filters.filter(filter => {
            // If this is a nested filter group
            if (filter.filters) {
                filter.filters = removeFiltersByField(filter.filters, fieldToRemove);
                // Keep the group if it still has filters
                return filter.filters.length > 0;
            }
            // For regular filters, remove if field matches
            return filter.field !== fieldToRemove;
        });
    };

    // Helper function to recursively add filters
    const addFiltersRecursively = (filters, newFilter) => {
        if (!filters) return [newFilter];

        // If we have existing filters, add to the root level
        return [...filters, newFilter];
    };

    //#region Lifecycle
    useEffect(() => {
        // Set selected partner from the table state
        if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
            const filter = table.state.filter.filters.find((filter) => filter.field === (isRetailer ? "supplierName" : "merchantName"));
            if (filter) {
                setSelectedPartner(
                    {
                        label: Array.isArray(filter.value) ? filter.value.join(", ") : filter.value,
                        value: filter.value
                    }
                );
            }
        } else {
            setSelectedPartner(null);
        }

        // Set active status from the table state
        if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
            const filter = table.state.filter.filters.find((filter) => filter.field === "merchantSku");
            if (filter) {
                setInventoryStatusSelection(filter.operator === "ne" ? "matched" : "unmatched");
            }
        } else {
            setInventoryStatusSelection("all");
        }

        // eslint-disable-next-line
    }, [table.state.filter]);

    useEffect(() => {
        // Get entity count using azure search top and count
        const getEntityCount = async () => {
            const response = await axios.get(`${Config.api}/odata/Company/Functions.ItemSearch?$top=0&$count=true`);
            setEntityCount(response.data["@odata.count"]);
        }

        getEntityCount();
    }, []);
    //#endregion

    return (
        <>
            <EnhancedSelect
                isClearable
                placeholder={"Select partner..."}
                containerStyle={{minWidth: "100px"}}
                options={
                    partners.map((partner) => ({
                        label: partner.CompanyName,
                        value: partner.Id
                    }))
                }
                value={selectedPartner}
                onChange={(value) => {
                    if (!value) {
                        table.extensions.onRemoveFilter({
                            field: isRetailer ? "supplierName" : "merchantName",
                            operator: 'eq',
                            value: selectedPartner.value
                        });
                    } else {
                        const oldFilters = table.state.filter?.filters?.filter((filter) => filter.field !== (isRetailer ? "supplierName" : "merchantName")) || [];
                        table.extensions.replaceFilters([
                            ...oldFilters,
                            {
                                field: isRetailer ? "supplierName" : "merchantName",
                                operator: 'eq',
                                value: value.label,
                            }
                        ]);
                    }

                    setSelectedPartner(value);
                }}
            />
            <>
                <Button
                    variant='outlined' color={inventoryStatusSelection === "all" ? "primary" : "default"}
                    onClick={() => {
                        if (inventoryStatusSelection !== "all") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const newFilters = removeFiltersByField(table.state.filter.filters, "merchantSku");
                                table.extensions.replaceFilters(newFilters);
                            }
                        }

                        setInventoryStatusSelection("all");
                    }}
                >
                    All ({(entityCount || 0)})
                </Button>

                <Button
                    variant='outlined' color={inventoryStatusSelection === "matched" ? "primary" : "default"}
                    onClick={() => {
                        if (inventoryStatusSelection !== "matched") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const newFilters = removeFiltersByField(table.state.filter.filters, "merchantSku");
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: null
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: "",
                                        }
                                    ],
                                    logic: "and",
                                    static: true
                                };

                                const updatedFilters = addFiltersRecursively(newFilters, unmatchedFilter);
                                table.extensions.replaceFilters(updatedFilters);
                            } else {
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: null
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: "",
                                            static: true
                                        }
                                    ],
                                    logic: "and",
                                    static: true
                                };

                                table.extensions.replaceFilters([unmatchedFilter]);
                            }
                        }

                        setInventoryStatusSelection("matched");
                    }}
                >
                    Matched SKUs
                </Button>

                <Button
                    variant='outlined' color={inventoryStatusSelection === "unmatched" ? "primary" : "default"}
                    onClick={() => {
                        if (inventoryStatusSelection !== "unmatched") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const newFilters = removeFiltersByField(table.state.filter.filters, "merchantSku");
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: null
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: "",
                                        }
                                    ],
                                    logic: "or",
                                    static: true
                                };

                                const updatedFilters = addFiltersRecursively(newFilters, unmatchedFilter);
                                table.extensions.replaceFilters(updatedFilters);
                            } else {
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: null
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: "",
                                            static: true
                                        }
                                    ],
                                    logic: "or",
                                    static: true
                                };

                                table.extensions.replaceFilters([unmatchedFilter]);
                            }
                        }

                        setInventoryStatusSelection("unmatched");
                    }}
                >
                    Unmatched SKUs
                </Button>
            </>
        </>
    );
}

function TableMoreOpts(props) {
    const {row, setInventoryEntity, setInventoryEditorOpen, setInventoryDeleteEntities, setInventoryDeleteOpen, setInventoryBulkEntities} = props;
    const {hasInventoryManagePermission} = props;

    return (
        <>
            <Button
                onClick={() => {
                    setInventoryEntity(row);
                    setInventoryEditorOpen(true);
                }}
            >
                <div style={{display: "flex", gap: "12px", alignItems: "flex-start", fontSize: "20px", width: "100%"}}>
                    <Edit style={{width: "20px", height: "20px", display: "flex", alignItems: "center"}}/>
                    <Typography>
                        {hasInventoryManagePermission ? "Edit" : "View"}
                    </Typography>
                </div>
            </Button>

            {hasInventoryManagePermission && (
                <Button
                    onClick={() => {
                        setInventoryBulkEntities([row]); // For zeroing out inventory
                        setInventoryDeleteEntities([row]);
                        setInventoryDeleteOpen(true);
                    }}
                >
                    <div style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "flex-start",
                        fontSize: "20px",
                        width: "100%"
                    }}>
                        <Delete style={{width: "20px", height: "20px", display: "flex", alignItems: "center"}}/>
                        <Typography>Delete</Typography>
                    </div>
                </Button>
            )}
        </>
    )
}

const BulkActions = (props) => {
    const {
        setInventoryDeleteEntities, setInventoryDeleteOpen,
        setInventoryBulkEntities, setInventoryBulkOpen, setInventoryBulkAnchorEl,
        setInventoryUnmatchConfirm, isBillable
    } = props;
    const globalClasses = useGlobalStyles();

    return (
        <ButtonGroup className={globalClasses.buttonGroup} variant="outlined"
                     style={{
                         visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                         opacity: props.table.state.selected.length > 0 ? 1 : 0,
                     }}>
            <Button variant='outlined' onClick={(e) => {
                const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                setInventoryBulkEntities(selectedEntities);
                setInventoryBulkOpen(true);
                setInventoryBulkAnchorEl(e.currentTarget);
            }}>
                Bulk Edit
            </Button>

            {isBillable && (
                <Button variant='outlined' onClick={(e) => {
                    const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                    setInventoryBulkEntities(selectedEntities);
                    setInventoryUnmatchConfirm(true);
                }}>
                    Unmatch
                </Button>
            )}

            <Button variant='outlined' onClick={() => {
                const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                setInventoryDeleteEntities(selectedEntities);
                setInventoryDeleteOpen(true);
            }}>
                Delete
            </Button>
        </ButtonGroup>
    )
}

function BulkEditPopper({
                            open,
                            anchorEl,
                            onClose,
                            catalogStyles,
                            globalClasses,
                            handleBulkEdit,
                            zeroOutInventory,
                            setZeroOutConfirm
                        }) {
    const [quantity, setQuantity] = React.useState(null);
    const [cost, setCost] = React.useState(null);

    const [bulkEditConfirm, setBulkEditConfirm] = React.useState(false);

    useEffect(() => {
        setQuantity(null);
        setCost(null);
    }, [open]);

    return (
        <>
            <Popper open={open} anchorEl={anchorEl} transition placement={"bottom-start"}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={onClose}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Grid container className={catalogStyles.bulkUpdateStatus}>
                                <Typography>Bulk edit selected items</Typography>

                                <EnhancedTextField
                                    label="Quantity"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    numeric={true}
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0
                                        }
                                    }}
                                />

                                <EnhancedTextField
                                    label="Cost"
                                    value={cost}
                                    onChange={(e) => setCost(e.target.value)}
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0
                                        }
                                    }}
                                />

                                <Grid item container style={{width: "100%", gap: "8px"}} justify={"flex-end"}>
                                    <Button
                                        style={{padding: "6px 12px"}}
                                        onClick={onClose}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            setZeroOutConfirm(true);
                                        }}
                                        variant="outlined"
                                    >
                                        Zero Out
                                    </Button>

                                    <Button
                                        color="primary"
                                        variant="outlined"
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            if (!quantity && !cost) return;
                                            setBulkEditConfirm(true);
                                        }}
                                        disabled={!quantity && !cost}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>

            <EnhancedConfirmDialog
                open={bulkEditConfirm}
                setOpen={setBulkEditConfirm}
                title="Bulk Edit"
                message="This action cannot be undone. Are you sure you want to update the selected SKUs?"
                type={"caution"}
                action={(action) => {
                    if (action) {
                        handleBulkEdit(quantity, cost);
                    }
                    setBulkEditConfirm(false);
                    onClose();
                }}
            />
        </>
    );
}

const SkuHoverTemplate = React.memo(({sku}) => {
    const [isHighlighted, setIsHighlighted] = React.useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(sku);
        setIsHighlighted(true);
        setTimeout(() => setIsHighlighted(false), 1000);
    };

    return (
        <Grid container style={{flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "4px"}}>
            <Typography style={{
                backgroundColor: isHighlighted ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'background-color 0.2s ease',
                padding: '2px 4px',
                borderRadius: '2px'
            }}>
                {sku}
            </Typography>
            {sku && (
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    style={{ padding: "4px", marginBottom: "4px"}}
                >
                    <ClipboardCopyIcon width="18px" height="18px" />
                </IconButton>
            )}
        </Grid>
    );
});

function InventoryManagementCenter(props) {
    let urlCatalog = new Helpers().getUrlObject('catalog');
    const {isBillable} = props;

    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const filterNullExceptions = ["merchantSku"];

    const [inventoryConfig, setInventoryConfig] = React.useState({
        url: Config.api + "/odata/Company/Functions.ItemSearch",
        columns: [
            {id: "id", hidden: true},
            {id: "supplierId", hidden: true},
            {id: "merchantId", hidden: true},
            {
                id: 'supplierName',
                filterable: true,
                hidden: !props.isRetailer,
                label: 'Partner',
                width: '16em',
                getOptions: getCompanyNames
            },
            {
                id: 'merchantName',
                filterable: true,
                hidden: props.isRetailer,
                label: 'Partner',
                width: '16em',
                getOptions: getCompanyNames,
            },
            {
                id: 'supplierSku',
                sortable: true,
                filterable: true,
                label: 'Supplier SKU',
                width: '12em',
                toggleable: true,
                hoverTemplate: (row) => <SkuHoverTemplate sku={row.supplierSku} />,
                hoverTemplateInteractive: true,
                hoverTemplateLeaveDelay: 750
            },
            {
                id: 'merchantSku',
                sortable: true,
                filterable: true,
                label: 'Retailer SKU',
                width: '12em',
                toggleable: true,
                hoverTemplate: (row) => <SkuHoverTemplate sku={row.merchantSku} />,
                hoverTemplateInteractive: true,
                hoverTemplateLeaveDelay: 750
            },
            {id: 'quantity', sortable: true, type: 'number', filterable: true, label: 'Quantity', width: '8em'},
            {
                id: 'vendorNumber',
                toggleable: true,
                filterable: true,
                hidden: true,
                sortable: true,
                label: 'Vendor Number',
                width: '12em'
            },
            {id: 'upc', hidden: true, toggleable: true, sortable: true, filterable: true, label: 'UPC', width: '12em'},
            {
                id: 'manufacturerSku',
                hidden: true,
                toggleable: true,
                sortable: true,
                filterable: true,
                label: 'Mfr. SKU',
                width: '12em'
            },
            {
                id: 'cost',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                label: 'Cost',
                width: '8em'
            },
            {
                id: 'retailPrice',
                sortable: true,
                toggleable: true,
                hidden: true,
                type: 'number',
                filterable: true,
                label: 'Retail Price',
                width: '8em'
            },
            {id: 'supplierDescription', toggleable: true, label: 'Description', align: 'left', width: '16em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.supplierDescription}
                        </Typography>
                    )
                }
            },
            {
                id: 'size',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Size',
                width: '8em'
            },
            {
                id: 'color',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Color',
                width: '8em'
            },
            {
                id: 'style',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Style',
                width: '8em'
            },
            {
                id: 'weight',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                hidden: true,
                label: 'Weight',
                width: '8em'
            },
            {id: 'warehouse', hidden: true, toggleable: true, filterable: true, label: 'Warehouse', width: '12em'},
            {
                id: 'nextAvailableDate',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Next Available Date',
                width: '16em'
            },
            {
                id: 'nextAvailableQuantity',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                hidden: true,
                label: 'Next Available Qty',
                width: '12em'
            },
            {
                id: 'lastUpdate',
                type: 'date',
                toggleable: true,
                filterable: true,
                sortable: true,
                label: 'Refreshed within',
                template: (row) => {
                    const utcValue = moment.utc(row.lastUpdate);
                    return moment(utcValue).local().fromNow().replace(' ago', '');
                },
                width: '15em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {moment(row.lastUpdate).format('MM/DD/YY hh:mmA')}
                        </Typography>
                    )
                },
                alwaysShowTooltip: true
            },
            {
                id: 'status',
                hidden: true,
                toggleable: true,
                sortable: true,
                filterable: true,
                label: 'Status',
                width: '12em'
            }
        ],
        keyField: 'id',
        order: 'asc',
        orderBy: 'supplierSku',
        pageSize: 25,
        pageSizes: [25, 50, 100],
        title: 'Inventory Search',
        refresh: false,
        filterInUrl: true,
        filter: null
    });
    const [eventsConfig, setEventsConfig] = React.useState({
        url: Config.api + "/odata/Company/VwActivityEvents",
        columns: [
            {
                id: 'EventDate',
                type: 'date',
                filterable: true,
                sortable: true,
                label: 'Date',
                template: (row, column, colIndex, rowIndex) => (
                    <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
                ),
                width: '15em'
            },
            {id: 'PartnerCoId', label: "Partner CoId", hidden: true, filterable: false, sortable: true, width: '8em'},
            {
                id: 'PartnerCompanyName',
                filterable: true,
                sortable: true,
                label: 'Partner',
                width: '12em',
                alwaysShowTooltip: true,
                template: (row) => row.PartnerCompanyName || "All Partners",
                getOptions: getCompanyNames,
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.PartnerCompanyName || "This event is not associated with a specific partner."}
                        </Typography>
                    )
                }
            },
            {id: 'EventLevel', label: 'Severity', width: '12em', filterable: true, sortable: true},
            {id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true, width: '16em'},
            {id: 'Details', label: 'Details', filterable: true, allowStringOperators: true, width: '16em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.Details}
                        </Typography>
                    )
                }
            },
            {
                id: 'Viewed', label: "Viewed", sortable: true, hidden: true, filterable: false, template: (row, column, colIndex, rowIndex) => {
                    return (
                        <CatalogCheckbox checked={row.Viewed} disabled={true}/>
                    )
                }
            },
            {id: 'EventTypeId', hidden: true},
            {
                id: 'Id',
                width: '10em',
                stopPropagation: 'true',
                filterable: false,
                template: (row, column, colIndex, rowIndex, onAddFilter, setEventId, setEventViewed, setEventDialogOpen) =>
                    <EventIdColumn
                        row={row} setEventId={setEventId} setEventViewed={setEventViewed}
                        setEventDialogOpen={setEventDialogOpen}/>
            },
            {id: 'CustomerViewable', hidden: true, filterable: false},
        ],
        order: 'desc',
        orderBy: 'Id',
        keyField: 'Id',
        pageSize: 25,
        pageSizes: [25, 50, 100],
        title: "Event Log",
        refresh: false,
        filterInUrl: false,
        filter: {
            filters: [
                // {field: 'EventTypeId', operator: 'eq', value: 39, static: true},
                {field: "Category", operator: 'eq', value: 'Inventory', static: true},
                // {field: "PartnerCoId", operator: 'eq', value: null, static: true}
            ], logic: 'and', static: true
        },
    });

    const catalogStyles = useStyles();

    const [currentTab, setCurrentTab] = React.useState(urlCatalog || "feed");
    const [loading, setLoading] = React.useState(false);

    const [inventoryEntity, setInventoryEntity] = React.useState(null);
    const [inventoryEditorOpen, setInventoryEditorOpen] = React.useState(false);

    const [inventoryDeleteEntities, setInventoryDeleteEntities] = React.useState([]);
    const [inventoryDeleteOpen, setInventoryDeleteOpen] = React.useState(false);
    const [inventoryDeleting, setInventoryDeleting] = React.useState(false);

    const [inventoryBulkEntities, setInventoryBulkEntities] = React.useState([]);
    const [inventoryBulkOpen, setInventoryBulkOpen] = React.useState(false);
    const [inventoryBulkAnchorEl, setInventoryBulkAnchorEl] = React.useState(null);
    const [inventoryBulkUpdating, setInventoryBulkUpdating] = React.useState(false);
    const [inventoryZeroConfirm, setInventoryZeroConfirm] = React.useState(false);
    const [inventoryUnmatchConfirm, setInventoryUnmatchConfirm] = React.useState(false);

    const [inventoryStatusSelection, setInventoryStatusSelection] = React.useState("all");
    const [inventorySummary, setInventorySummary] = React.useState({
        lastUpdate: "",
        lastUpdateCount: ""
    });

    const [error, setError] = React.useState({
        open: false,
        errorHeader: "",
        errorMessage: ""
    });

    const [partners, setPartners] = React.useState([]);
    const [helpDesks, setHelpDesks] = React.useState([]);
    const [ediSpecifications, setEdiSpecifications] = React.useState([]);
    const [rules, setRules] = React.useState([]);

    const globalClasses = useGlobalStyles();

    //#region Helper Functions
    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setPartners(sortedPartners);
        setLoading(false);
    }

    const deleteInventory = async () => {
        if (!hasInventoryManagePermission) return;

        setInventoryDeleting(true);
        let errors = [];

        try {
            for (const entity of inventoryDeleteEntities) {
                const response = await utilDeleteInventory(
                    props.isRetailer ? entity.supplierId : entity.merchantId,
                    encodeURIComponent(entity.supplierSku)
                );
                if (response !== true && response.errorMessage) {
                    errors.push(response.errorMessage);
                }
            }

            if (errors.length > 0) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: errors.join("\n")
                });
            }

            await new Promise(resolve => setTimeout(resolve, 750));
        } finally {
            setInventoryDeleteOpen(false);
            setInventoryDeleting(false);
            
            // Wait a bit to ensure state updates are complete
            await new Promise(resolve => setTimeout(resolve, 100));
            
            setInventoryConfig(prevConfig => ({...prevConfig, refresh: !prevConfig.refresh}));
        }
    };

    const zeroOutInventory = async () => {
        if (!hasInventoryManagePermission) return;

        handleBulkEdit(0, null, null);
    }

    const unmatchInventory = async () => {
        if (!hasInventoryManagePermission) return;

        handleBulkEdit(null, null, "");
    }

    const handleBulkEdit = async (quantity, cost, merchantSku) => {
        if (!hasInventoryManagePermission) return;

        setInventoryBulkUpdating(true);

        let errors = [];

        for (const entity of inventoryBulkEntities) {
            const existingInventory = await utilCheckInventory(
                props.isRetailer ? entity.supplierId : entity.merchantId,
                encodeURIComponent(entity.supplierSku)
            );
            if (existingInventory.errorMessage) {
                errors.push(existingInventory.errorMessage);
                continue;
            }

            const response = await utilUpdateInventory(
                props.isRetailer ? entity.supplierId : entity.merchantId,
                {
                    ...existingInventory,
                    ...(quantity !== null && {Quantity: quantity}),
                    ...(cost !== null && {Cost: cost}),
                    ...(merchantSku !== null && {MerchantSku: merchantSku})
                }
            );
            if (response !== true && response.errorMessage) {
                errors.push(response.errorMessage);
            }
        }

        if (errors.length > 0) {
            setError({
                open: true,
                errorHeader: "Error bulk updating inventory",
                errorMessage: errors.join("\n")
            });
        }

        await new Promise(resolve => setTimeout(resolve, 1250));
        setInventoryBulkOpen(false);
        setInventoryBulkAnchorEl(null);
        setInventoryConfig({...inventoryConfig, refresh: !inventoryConfig.refresh});
        setInventoryBulkUpdating(false);
    }

    const refreshInventory = async () => {
        await new Promise(resolve => setTimeout(resolve, 750));
        setInventoryConfig({...inventoryConfig, refresh: !inventoryConfig.refresh});
    }

    const loadEdiSpecifications = async () => {
        try {
            let url = Config.api + '/odata/Company/EDIPartnerSpecs';
            let response = await axios.get(url);
            const specifications = response.data.value;
            setEdiSpecifications(specifications);
        } catch (err) {
            setError({
                open: true,
                errorHeader: "Error Loading EDI Specifications",
                errorMessage: err.message
            });
        }
    }

    const loadHelpDesks = async () => {
        try {
            let res = (await axios.get(Config.api + "/odata/Company/Functions.GetCompanyInfo")).data;

            let helpDesks = (res.Helpdesks || []).sort((a, b) => a.CompanyName > b.CompanyName ? 1 : -1);
            setHelpDesks(helpDesks);
        } catch (e) {
            let error = null;
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            if (!error && !e.response) {
                error = "Request failed: " + e.message;
            }

            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: error
            });

            return;
        }
    }

    const refreshInventorySummary = async (partnerId) => {
        if (!partnerId) {
            setInventorySummary({
                lastUpdate: "",
                lastUpdateCount: ""
            });
            return;
        }

        const summary = await utilGetInventorySummary(partnerId);
        if (summary.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: summary.errorMessage
            });
        } else {
            setInventorySummary(summary);
        }
    }

    const setupValidationRules = async () => {
        let rules = (await axios.get(Config.api + "/odata/Company/ValidationDtos")).data.value || [];
        rules.forEach(r => {
            r.Rules.forEach(rule => {
                processRule(rule);
                (rule.Conditions || []).forEach(processRule);
                let oldConditions = rule.Conditions || [];
                rule.Conditions = oldConditions.filter(x => !(x.Type === "required" && x.Field === "Identifier.LogicbrokerKey"));
                rule.Validation = GetRuleDescription({...rule});
                rule.Required = rule.Type === "required" && rule.Conditions.length === 0;
                if(rule.Conditions.length !== oldConditions.length){
                    rule.Validation = (rule.Validation + "\nThis field may be automatically populated by Logicbroker.").trim();
                    rule.Required = true;
                }
            });
            r.Rules = r.Rules.sort((a, b) => {
                if(a.Required !== b.Required){
                    return a.Required && !b.Required ? -1 : 1;
                }
                return a.Field > b.Field ? 1 : a.Field < b.Field ? -1 : 0;
            });
        });

        const filteredRules = rules.filter(r => r.DocumentType === "Inventory");
        setRules(filteredRules);
    }

    const processRule = (rule) => {
        rule.DescriptiveField = rule.Field;
        rule.Parameters = rule.Parameters ? rule.Parameters.reduce((map, obj) => {map[obj.Key] = obj.Value; return map;}, {}) : null;
    }
    //#endregion

    React.useEffect(() => {
        loadPartners();
        loadHelpDesks();
        loadEdiSpecifications();
        setupValidationRules();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Grid container className={globalClasses.root} style={{gap: "24px", padding: "24px 14px 24px 14px"}}>
                <Grid container justify={"space-between"} direction={"row"}>
                    <Grid item container xs={6} justify={"flex-start"} alignItems={"center"}>
                        <Typography className={globalClasses.header}>
                            Inventory
                        </Typography>
                    </Grid>

                    <Grid item container xs={6} justify={"flex-end"} alignItems={"center"} style={{gap: "8px"}}>
                        {isBillable && (
                            <Button
                                style={{padding: "10px 20px"}}
                                variant='contained'
                                startIcon={<Settings style={{height: "18px", width: "18px"}}/>}
                                component={Link}
                                to={'/advanced-product-management/settings'}
                            >
                                Settings
                            </Button>
                        )}

                        <Button
                            style={{padding: "10px 20px"}}
                            variant='contained'
                            startIcon={<ExportIcon style={{height: "18px", width: "18px"}}/>}
                            component={Link}
                            to={'/advanced-product-management/export'}
                        >
                            Export
                        </Button>

                        <Button
                            style={{padding: "10px 20px"}}
                            variant='contained'
                            startIcon={<AddCircleOutline style={{height: "18px", width: "18px"}}/>}
                            onClick={() => {
                                setInventoryEditorOpen(true);
                                setInventoryEntity(null);
                            }}
                        >
                            Add Item
                        </Button>

                        {hasInventoryManagePermission && (
                            <Button
                                style={{padding: "10px 20px"}}
                                variant='contained'
                                color='primary'
                                startIcon={<ImportIcon style={{height: "18px", width: "18px"}}/>}
                                component={Link}
                                to={'/advanced-product-management/import'}
                            >
                                Import
                            </Button>
                        )}
                    </Grid>
                </Grid>

                <Grid container style={{gap: "12px"}}>
                    <Paper>
                        <CatalogTab title={"Feed"}
                                    description={"View and manage inventory"}
                                    selected={currentTab === "feed"}
                                    onClick={() => setCurrentTab("feed")}
                        />
                    </Paper>

                    <Paper>
                        <CatalogTab title={"Events"}
                                    description={"View uploaded/imported feeds and see errors"}
                                    selected={currentTab === "events"}
                                    onClick={() => setCurrentTab("events")}
                        />
                    </Paper>
                </Grid>

                {loading ? (
                    <>
                        <ContentLoader preserveAspectRatio='none' style={{height: '100px', width: "100%"}}
                                       primaryColor='#e1e1e1'
                                       secondaryColor='#d8d8d8'/>

                        <ContentLoader preserveAspectRatio='none' style={{height: '100%', width: "100%"}}
                                       primaryColor='#e1e1e1'
                                       secondaryColor='#d8d8d8'/>
                    </>
                ) : (
                    <>
                        {currentTab === "feed" && (
                            <EnhancedTable
                                config={inventoryConfig} history={props.history} location={props.location}
                                key={"inventorymanagement_table"}
                                template={(table) => {
                                    return (
                                        <CatalogManagerResults
                                            bulkActions={
                                                hasInventoryManagePermission && (
                                                    <BulkActions
                                                        table={table}
                                                        setConfig={setInventoryConfig}
                                                        setEventConfig={setEventsConfig}
                                                        setInventoryDeleteEntities={setInventoryDeleteEntities}
                                                        setInventoryDeleteOpen={setInventoryDeleteOpen}
                                                        setInventoryBulkEntities={setInventoryBulkEntities}
                                                        setInventoryBulkOpen={setInventoryBulkOpen}
                                                        setInventoryBulkAnchorEl={setInventoryBulkAnchorEl}
                                                        setInventoryUnmatchConfirm={setInventoryUnmatchConfirm}
                                                        isBillable={isBillable}
                                                    />
                                                )
                                            }
                                            isSelectable={hasInventoryManagePermission}
                                            disableTermInUrl={true}
                                            disableLoadingPlaceholder={true}
                                            condensed={false}
                                            history={props.history} location={props.location}
                                            table={table}
                                            config={inventoryConfig} setConfig={setInventoryConfig}
                                            filterNullExceptions={filterNullExceptions}
                                            resultsContainerStyles={{gap: "1em"}}
                                            renderButtonFiltersAfter={false}
                                            moreTableOptions={
                                                (row) =>
                                                    <TableMoreOpts
                                                        row={row}
                                                        setInventoryEntity={setInventoryEntity}
                                                        setInventoryEditorOpen={setInventoryEditorOpen}
                                                        setInventoryBulkEntities={setInventoryBulkEntities}
                                                        setInventoryBulkOpen={setInventoryBulkOpen}
                                                        setInventoryDeleteEntities={setInventoryDeleteEntities}
                                                        setInventoryDeleteOpen={setInventoryDeleteOpen}
                                                        hasInventoryManagePermission={hasInventoryManagePermission}
                                                    />
                                            }
                                            bufferExtensions={
                                                inventoryStatusSelection !== "all" && (
                                                    <Alert severity="info" style={{width: "100%"}}>
                                                        {inventoryStatusSelection === "matched"
                                                            ? (
                                                                <Typography>
                                                                    This view will show all items that have a retailer SKU.
                                                                    For more information on matched SKUs, see our {' '}
                                                                    <a
                                                                        href={"https://help.logicbroker.com/hc/en-us/articles/360022068531-Inventory-Feeds"}
                                                                        target={"_blank"} rel={"noopener noreferrer"}
                                                                    >
                                                                            Inventory
                                                                    </a> {' '}
                                                                    article.
                                                                </Typography>
                                                            )
                                                            : (
                                                                <Typography>
                                                                    This view will show all items that do not have a
                                                                    retailer SKU. For more information on matched SKUs, see
                                                                    our {' '}
                                                                    <a
                                                                        href={"https://help.logicbroker.com/hc/en-us/articles/360022068531-Inventory-Feeds"}
                                                                        target={"_blank"} rel={"noopener noreferrer"}
                                                                    >
                                                                        Inventory
                                                                    </a> {' '}
                                                                    article.
                                                                </Typography>
                                                            )}
                                                    </Alert>
                                                )
                                            }
                                            buttonFilters={
                                                <Grid container style={{gap: "0.5em", flex: 1}}>
                                                    <InventoryStatus
                                                        isRetailer={props.isRetailer}
                                                        partners={partners}
                                                        table={table}
                                                        entityCount={table.state.total}
                                                        setInventoryStatusSelection={setInventoryStatusSelection}
                                                        inventoryStatusSelection={inventoryStatusSelection}
                                                    />
                                                </Grid>
                                            }
                                            tableMessage={
                                                table.data.length > 0 ? null : (
                                                    <TableCell colSpan={inventoryConfig.columns.length}>
                                                        <Typography style={{
                                                            textAlign: "center",
                                                            color: "#4F4D55",
                                                            margin: "1em 0"
                                                        }}>
                                                            <b>No inventory data found</b>
                                                        </Typography>
                                                    </TableCell>
                                                )
                                            }
                                        />
                                    );
                                }}
                            />
                        )}

                        {currentTab === "events" && (
                            <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                                           template={(table) =>
                                               <>
                                                   {inventorySummary.lastUpdate && (
                                                       <Grid container style={{marginBottom: "-20px", gap: "1em"}}>
                                                           <Typography style={{color: "#4F4D55"}}>
                                                               <b>Last File Imported
                                                                   On:</b> {inventorySummary.lastUpdate}
                                                           </Typography>
                                                           <Typography style={{color: "#4F4D55"}}>
                                                               <b>Refreshed
                                                                   Items:</b> {inventorySummary.lastUpdateCount}
                                                           </Typography>
                                                       </Grid>
                                                   )}
                                                   <CatalogEventResults
                                                       disableOnViewAction={true}
                                                       history={props.history} location={props.location}
                                                       currentCatalog={currentTab}
                                                       table={table} config={eventsConfig}
                                                       setConfig={setEventsConfig}
                                                       buttonFilters={
                                                           <Grid container style={{gap: "0.5em", flex: 1}}>
                                                               <InventoryEventsStatus
                                                                   table={table}
                                                                   partners={partners}
                                                                   refreshInventorySummary={refreshInventorySummary}
                                                               />
                                                           </Grid>
                                                       }
                                                   />
                                               </>
                                           }/>
                        )}
                    </>
                )}
            </Grid>

            <InventoryEntity
                isRetailer={props.isRetailer}
                open={inventoryEditorOpen}
                setOpen={setInventoryEditorOpen}
                inventoryEntity={inventoryEntity}
                partners={partners}
                setError={setError}
                refreshInventory={refreshInventory}
                helpDesks={helpDesks}
                ediSpecifications={ediSpecifications}
                hasInventoryManagePermission={hasInventoryManagePermission}
                rules={rules}
            />

            <BulkEditPopper
                open={inventoryBulkOpen}
                anchorEl={inventoryBulkAnchorEl}
                onClose={() => {
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);
                }}
                handleBulkEdit={handleBulkEdit}
                zeroOutInventory={zeroOutInventory}
                setZeroOutConfirm={setInventoryZeroConfirm}
                catalogStyles={catalogStyles}
                globalClasses={globalClasses}
            />

            <UpdatingDialog
                open={inventoryDeleting || inventoryBulkUpdating}
                setOpen={inventoryDeleting ? setInventoryDeleting : setInventoryBulkUpdating}
                title={inventoryDeleting ? "Deleting Inventory..." : "Updating Inventory..."}
                subtitle={inventoryDeleting ? "Please wait while we delete the inventory..." : "Please wait while we update the inventory..."}
            />

            <EnhancedConfirmDialog
                open={inventoryZeroConfirm}
                setOpen={setInventoryZeroConfirm}
                title="Zero Out Items?"
                message="This action cannot be undone. Are you sure you want to update the quantity for all SKUs to 0?"
                type={"caution"}
                action={(action) => {
                    if (action) {
                        zeroOutInventory();
                    }
                    setInventoryZeroConfirm(false);
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);

                    setInventoryDeleteOpen(false);
                }}
            />

            <EnhancedConfirmDialog
                open={inventoryUnmatchConfirm}
                setOpen={setInventoryUnmatchConfirm}
                title="Unmatch Items?"
                message={(
                    <Typography>
                        Unmatching items will remove the Retailer SKU from the item and the item will no longer be
                        shared with the retailer.
                        Unmatch items that updates should no longer be sent for.
                        <br/>
                        <br/>
                        Reach out to your partner to review the process to discontinue items.
                    </Typography>
                )}
                type={"delete"}
                customConfirmText={"Unmatch"}
                action={(action) => {
                    if (action) {
                        unmatchInventory();
                    }
                    setInventoryUnmatchConfirm(false);
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);
                }}
            />

            <EnhancedConfirmDialog open={inventoryDeleteOpen} setOpen={setInventoryDeleteOpen}
                                   title={"Delete Inventory?"}
                                   message={(
                                       <Typography>
                                           Before deleting items, please be sure to zero out the inventory for those
                                           items. Failure to do so may cause stock outs with remaining quantity in the
                                           retailer's system. Please make sure these items are removed from future
                                           feeds.
                                           <br/>
                                           <br/>
                                           Confirm the process to discontinue items with your partner.
                                       </Typography>
                                   )}
                                   type={"delete"}
                                   action={(action) => {
                                       if (action) deleteInventory()
                                   }}
                                   extraActionsBetween={(
                                       <Button variant='outlined' onClick={() => {
                                           setInventoryZeroConfirm(true);
                                           setInventoryBulkOpen(false);
                                           setInventoryBulkAnchorEl(null);
                                       }}>
                                           Zero Out Inventory
                                       </Button>
                                   )}
            />

            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </>
    );
}

export default InventoryManagementCenter;
